import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EomAssembleResult, EomReadiness } from '@accounting/shared';
import { api } from '../lib/api';

export const eomReadinessKey = (companyId: string | null, month: string) =>
  ['eom-financials', 'readiness', companyId ?? 'none', month] as const;

/** Is this month reconciled, and what would go into the packet? */
export function useEomReadiness(companyId: string | null, month: string) {
  return useQuery({
    queryKey: eomReadinessKey(companyId, month),
    enabled: companyId !== null && /^\d{4}-(0[1-9]|1[0-2])$/.test(month),
    queryFn: () =>
      api
        .get<{ readiness: EomReadiness }>(
          `/companies/${companyId}/eom-financials/readiness?month=${month}`,
        )
        .then((r) => r.readiness),
  });
}

export function useAssembleEomPacket(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (month: string) =>
      api
        .post<{ result: EomAssembleResult }>(`/companies/${companyId}/eom-financials/assemble`, {
          month,
        })
        .then((r) => r.result),
    onSuccess: (_r, month) => {
      qc.invalidateQueries({ queryKey: eomReadinessKey(companyId, month) });
      // The packet is filed in the vault, so the Documents view is now stale.
      qc.invalidateQueries({ queryKey: ['vault'] });
    },
  });
}
